import React, { useState, useEffect } from 'react';
import { TruckIcon, PlusIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import { Vehicle } from '../../types/vehicle';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useOrganizations } from '../../hooks/useOrganizations';
import CommonPageHeader from '../common/CommonPageHeader';
import SearchBar from '../common/SearchBar';
import LoadingScreen from '../common/LoadingScreen';
import VehicleEditor from './VehicleEditor';
import VehicleDetails from './VehicleDetails';

const CustomerVehicles: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { organizations } = useOrganizations();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [showEditor, setShowEditor] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Real-time listener for vehicles
  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const vehiclesRef = collection(db, 'vehicles');
    const constraints = [
      where('organizationId', '==', user.organizationId),
      orderBy('licensePlate', 'asc')
    ];

    const q = query(vehiclesRef, ...constraints);

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const vehiclesData = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          purchaseDate: doc.data().purchaseDate?.toDate(),
          nextServiceDate: doc.data().nextServiceDate?.toDate(),
          insuranceExpiryDate: doc.data().insuranceExpiryDate?.toDate(),
          inspectionExpiryDate: doc.data().inspectionExpiryDate?.toDate(),
          documents: doc.data().documents?.map((d: any) => ({
            ...d,
            uploadedAt: d.uploadedAt?.toDate() || new Date(),
            expiryDate: d.expiryDate?.toDate()
          })) || [],
          photos: doc.data().photos?.map((p: any) => ({
            ...p,
            uploadedAt: p.uploadedAt?.toDate() || new Date()
          })) || []
        })) as Vehicle[];

        // Denormalizáljuk a hiányzó customerName mezőket
        const vehiclesWithNames = await Promise.all(
          vehiclesData.map(async (vehicle) => {
            if (vehicle.customerId && !vehicle.customerName) {
              try {
                const { getDoc, doc } = await import('firebase/firestore');
                const orgDoc = await getDoc(doc(db, 'organizations', vehicle.customerId));
                if (orgDoc.exists()) {
                  return {
                    ...vehicle,
                    customerName: orgDoc.data().name
                  };
                }
              } catch (error) {
                console.error('Error fetching organization name:', error);
              }
            }
            return vehicle;
          })
        );

        setVehicles(vehiclesWithNames);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading vehicles:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.organizationId]);

  // Szűrés
  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      vehicle.licensePlate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.customerName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCustomer = !selectedCustomer || vehicle.customerId === selectedCustomer;

    return matchesSearch && matchesCustomer;
  });

  // Csoportosítás ügyfél szerint
  const vehiclesByCustomer = filteredVehicles.reduce((acc, vehicle) => {
    const customerId = vehicle.customerId || 'no-customer';
    const customerName = vehicle.customerName || vehicle.ownerName || 'Nincs tulajdonos';
    
    if (!acc[customerId]) {
      acc[customerId] = {
        customerId,
        customerName,
        vehicles: []
      };
    }
    acc[customerId].vehicles.push(vehicle);
    return acc;
  }, {} as Record<string, { customerId: string; customerName: string; vehicles: Vehicle[] }>);

  const handleView = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetails(true);
  };

  const handleEdit = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowEditor(true);
  };

  const handleAddVehicle = (customerId?: string) => {
    if (customerId && customerId !== 'no-customer') {
      // Pre-fill customerId
      setSelectedVehicle({
        customerId,
        licensePlate: '',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        fuelType: 'gasoline',
        mileage: 0,
        status: 'active',
        documents: [],
        photos: []
      } as any);
    } else {
      setSelectedVehicle(null);
    }
    setShowEditor(true);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <CommonPageHeader
        title="Ügyfél Járművei"
        subtitle="Járművek kezelése ügyfél szerint csoportosítva"
        icon={UserIcon}
      >
        <button
          onClick={() => handleAddVehicle()}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>Új jármű</span>
        </button>
      </CommonPageHeader>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Keresés rendszám, márka, modell vagy ügyfél alapján..."
            />
          </div>

          {/* Customer Filter */}
          <div className="sm:w-64">
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
            >
              <option value="">Összes ügyfél</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          {filteredVehicles.length} jármű • {Object.keys(vehiclesByCustomer).length} ügyfél
        </div>
      </div>

      {/* Vehicles by Customer */}
      {Object.keys(vehiclesByCustomer).length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Nincs jármű</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Kezdj el járműveket hozzáadni az ügyfelekhez
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.values(vehiclesByCustomer).map((group) => (
            <div key={group.customerId} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Customer Header */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                      <UserIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {group.customerName}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {group.vehicles.length} jármű
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAddVehicle(group.customerId)}
                    className="inline-flex items-center px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-md text-sm font-medium text-blue-700 dark:text-blue-400 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Jármű hozzáadása
                  </button>
                </div>
              </div>

              {/* Vehicles Grid */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.vehicles.map((vehicle) => (
                    <div
                      key={vehicle.id}
                      onClick={() => handleView(vehicle)}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 transition-all cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {vehicle.photos && vehicle.photos.length > 0 ? (
                            <img
                              src={vehicle.photos[0].url}
                              alt={vehicle.licensePlate}
                              className="w-12 h-12 rounded object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center">
                              <TruckIcon className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {vehicle.licensePlate}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {vehicle.make} {vehicle.model}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">
                          {vehicle.mileage.toLocaleString()} km
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">
                          {vehicle.year}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showEditor && (
        <VehicleEditor
          vehicle={selectedVehicle}
          onClose={() => {
            setShowEditor(false);
            setSelectedVehicle(null);
          }}
        />
      )}

      {showDetails && selectedVehicle && (
        <VehicleDetails
          vehicle={selectedVehicle}
          onClose={() => {
            setShowDetails(false);
            setSelectedVehicle(null);
          }}
          onEdit={() => {
            setShowDetails(false);
            setShowEditor(true);
          }}
        />
      )}
    </div>
  );
};

export default CustomerVehicles;
